import { BaseSchema, boolean, object } from 'yup'
import { Customer, DeliveryInfo, CartPaymentType } from '../../types/orders'
import * as DynamicFormSection from '../DynamicForms/DynamicFormSection'
import * as CheckoutDeliveryInfo from './CheckoutDeliveryInfo/CheckoutDeliveryInfo'
import * as CustomerInfo from './CustomerInfo/CustomerInfo'
import * as Tip from './Tip/Tip'
import * as CompanyName from './CompanyName/CompanyName'
import { FulfillmentContextType } from '../FulfillmentProvider/FulfillmentProvider'
import { EventType, FormField, FormValue } from '../../types/form'
import { PreComputedTip, Cart } from '../../types/cart'
import { Customer as AuthedCustomer } from '../../auth/CustomerAuthContext'
import { TdsConfig, PayLaterOption } from '../../types/config'
import { SaveNewAddressRequest } from '../../api/cart'
import { CurrencyFormatter } from '@local/do-secundo-use-currency'
import { getCheckTotal } from '../../utils/cart-helpers'
import { CheckoutProps } from './useCheckoutProps'
import { ConfirmOrderError } from '../CheckoutPage/utils'
import { PhoneNumber } from '@toasttab/buffet-pui-phone-input'

// Helper to extract tip amount from form values
export const getTipAmount = (values: CheckoutFormValues): number => {
  return parseFloat(String(values.paymentTip)) || 0
}

// Determine initial payment type based on business rules
// - If TDS is in play for this order, always require a cc payment
// - Else if pay later is required, set payment type to OTHER to prevent cc form and tip
// - Else if the check total is 0, set to ZERO_DOLLAR_ORDER to allow tipping
// - Otherwise, default to CREDIT
export const getInitialPaymentType = (
  tdsConfig: TdsConfig | undefined,
  payLaterOption: PayLaterOption | undefined,
  cart: Cart
) => {
  if (tdsConfig?.enabled) return 'CREDIT'
  if (payLaterOption === PayLaterOption.REQUIRE) return 'OTHER'
  if (getCheckTotal(cart) === 0) return 'ZERO_DOLLAR_ORDER'
  return 'CREDIT'
}

// Type for checkout state derived from user form input
export interface UserInputDerivedState {
  canShowTip: boolean
  tipAmount: number
  requiresPayment: boolean
  showCreditCard: boolean
}

// Common props for both domestic and international checkout form bodies
export interface CheckoutFormBodyProps extends UserInputDerivedState {
  customer: CustomerInfo.Customer
  values: CheckoutFormValues
  isSubmitting: boolean
  canCheckout: boolean
  confirmOrderError?: ConfirmOrderError
  setEditingAccount: (editing: boolean) => void
  setIsAuthModalOpen: (value: boolean) => void
  isAuthModalOpen: boolean
  checkoutProps: CheckoutProps
  handleSubmit: () => void
  setFieldValue: (field: string, value: any) => void
}

// Calculate checkout state based on user's form input values
export const deriveCheckoutStateFromUserInput = ({
  values,
  cart,
  isPayLaterRequired
}: {
  values: CheckoutFormValues
  cart: Cart | undefined
  isPayLaterRequired: boolean
}): UserInputDerivedState => {
  const canShowTip =
    values.paymentType !== 'OTHER' && (cart?.allowTipping ?? false)
  const tipAmount = canShowTip ? getTipAmount(values) : 0
  const cartHasBalance = cart ? getCheckTotal(cart) > 0 : false
  const requiresPayment = cartHasBalance || tipAmount > 0
  const showCreditCard = requiresPayment && !isPayLaterRequired

  return {
    canShowTip,
    tipAmount,
    requiresPayment,
    showCreditCard
  }
}

export const formModules = [
  CustomerInfo,
  CheckoutDeliveryInfo,
  Tip,
  DynamicFormSection,
  CompanyName
]

export const whenNewCredit = ({
  then,
  otherwise = object().optional()
}: {
  then: BaseSchema
  otherwise?: BaseSchema
}) =>
  object().when(['paymentType', 'paymentTip'], {
    is: (paymentType: CartPaymentType, paymentTip: number) => {
      if (paymentType !== 'CREDIT') return false
      if (paymentTip > 0) return true
      return true
    },
    then,
    otherwise
  })

export type PaymentValidationMethod = 'SPI' | 'ADYEN' | 'LEGACY'

export const getValidationSchema = ({
  fulfillmentContext,
  formFields,
  paymentValidationMethod,
  tdsConfig,
  cartAmount,
  currencyFormatter
}: {
  fulfillmentContext: FulfillmentContextType
  formFields: FormField[]
  paymentValidationMethod: PaymentValidationMethod
  tdsConfig: TdsConfig
  cartAmount: number
  currencyFormatter: CurrencyFormatter
}) => {
  // To be refactored into their own form modules.
  const baseSchemaValidationSchema = {
    encryptedCard:
      paymentValidationMethod === 'LEGACY'
        ? whenNewCredit({
            // The validation only seems to work with string() even though this value is
            // actually an object. Note that the value itself should be opaque since it
            // came from the iframe so we really only care whether it is present or not.
            then: object().required('Required')
          })
        : object().optional(),
    isPaymentInfoValid: boolean().when(['paymentType'], {
      is: (paymentType: CartPaymentType) =>
        paymentType === 'CREDIT' && paymentValidationMethod === 'SPI',
      then: boolean().oneOf([true], 'Payment info is not valid').required(),
      otherwise: boolean().optional()
    })
  }

  const validationSchema = formModules.reduce(
    (acc, formModule) => ({
      ...acc,
      ...(formModule.getValidationSchema
        ? formModule.getValidationSchema({
            fulfillmentContext,
            formFields,
            tdsConfig,
            cartAmount,
            currencyFormatter
          })
        : null)
    }),
    baseSchemaValidationSchema
  )

  return object().shape(validationSchema)
}

export interface ArgsForSubmit {
  paymentType: CartPaymentType
  paymentGuid?: string
  ccInput: {
    cartGuid?: string
    customerGuid?: string
    customer: CustomerInfo.Customer
    newAddress?: {
      deliveryInfo: DeliveryInfo
    }
    newCardInput?: CardInput
    tipAmount: number
  }
  formValues: FormValue[]
  loyaltyEnroll: boolean | undefined
  taxExemptId?: string
  taxExemptState?: string
  companyName: string | undefined
  shouldLookupLoyalty: boolean
  saveNewAddressRequest?: SaveNewAddressRequest
  marketingConsent: MarketingConsent
}

interface CardInput {
  encryptionKeyId: string
  encryptedCardDataString: string
  cardFirst6: string
  cardLast4: string
  expMonth: string
  expYear: string
  zipCode: string
}

export const getArgsForSubmit = ({
  fulfillmentContext,
  values,
  eventType
}: {
  fulfillmentContext: FulfillmentContextType
  values: CheckoutFormValues
  eventType: EventType
}): ArgsForSubmit => {
  let { paymentType } = values

  const customer = CustomerInfo.getArgsForSubmit({ values })

  const deliveryInfoArgs = CheckoutDeliveryInfo.getArgsForSubmit({
    fulfillmentContext,
    values
  })

  const tipArgs = Tip.getArgsForSubmit({
    values
  })

  const formArgs = DynamicFormSection.getArgsForSubmit(eventType, values)

  return {
    paymentType: paymentType,
    paymentGuid: values.paymentGuid,
    ccInput: {
      ...customer,
      ...deliveryInfoArgs,
      ...tipArgs,
      newCardInput: paymentType === 'CREDIT' ? values.encryptedCard : undefined
    },
    formValues: formArgs,
    loyaltyEnroll: values.loyaltyEnroll,
    companyName: values.companyName,
    shouldLookupLoyalty: values.optedInToLoyaltyLookup,
    saveNewAddressRequest: deliveryInfoArgs?.newAddress?.saveNewAddressRequest,
    marketingConsent: values.marketingConsent
  }
}

type MarketingConsent = {
  isEmailMarketingConsentGiven: boolean
  isSmsMarketingConsentGiven?: boolean
}

export interface CheckoutFormValues {
  encryptedCard: CardInput | undefined
  paymentType: CartPaymentType
  paymentTip: string | number
  paymentGuid?: string
  loyaltyEnroll: boolean | undefined
  companyName?: string
  preComputedTips?: PreComputedTip
  optedInToLoyaltyLookup: boolean
  isPaymentInfoValid: boolean
  customerFirstName?: string
  customerLastName?: string
  customerEmail?: string
  customerTel?: PhoneNumber
  marketingConsent: MarketingConsent
}

export interface InitialValuesArgs extends InitialCustomerValueArgs {
  fulfillmentContext: FulfillmentContextType
  formFields: FormField[]
  paymentType: CartPaymentType
  preComputedTips: PreComputedTip[] | undefined
  companyName: string | undefined
  smsMarkingetingEnabled?: boolean
  isIntlRx: boolean
  currentValues?: Partial<CheckoutFormValues>,
  restaurantCountry?: string
}

export interface InitialCustomerValueArgs {
  authedCustomer: AuthedCustomer | undefined
  customer: Customer | undefined
  restaurantCountry?: string
}

export const getInitialValues = (
  args: InitialValuesArgs
): CheckoutFormValues => {
  const { paymentType, currentValues } = args

  const isOptedInByDefault = !args.isIntlRx

  const marketingConsent: MarketingConsent = {
    isEmailMarketingConsentGiven: isOptedInByDefault
  }

  // only add the prop if SMS is enabled, but its unchecked by default
  if (args.smsMarkingetingEnabled) {
    marketingConsent.isSmsMarketingConsentGiven = false
  }

  const initialValues: CheckoutFormValues = {
    encryptedCard: undefined,
    paymentType,
    paymentTip: '',
    optedInToLoyaltyLookup: false,
    isPaymentInfoValid: false,
    loyaltyEnroll: false,
    marketingConsent
  }

  const computedValues = formModules.reduce(
    (acc, formModule) => ({
      ...acc,
      ...(formModule.getInitialValues
        ? formModule.getInitialValues(args)
        : null),
      gcNumber: ''
    }),
    initialValues
  )

  // Preserve dynamic form field values if they exist
  if (currentValues && args.formFields) {
    args.formFields.forEach((field) => {
      if (currentValues[field.guid] !== undefined) {
        computedValues[field.guid] = currentValues[field.guid]
      }
    })
  }

  return computedValues
}
